/**
 * The words one contact will actually get on one step.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * AN OVERRIDE SHADOWS THE ASSET. IT DOES NOT REPLACE THE REFERENCE TO IT.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `enrollment_steps.content_id` still points at the library row after an
 * edit, because it is the only thing that can answer "what was this changed
 * FROM", and the only link propagation.ts can match on. `subject` and `body`
 * on the step are nullable and mean "use the asset" when null.
 *
 * THE OVERRIDE IS A TEMPLATE, NOT RENDERED TEXT. It holds the same
 * `{{first_name}}` syntax the asset holds. The editor lets somebody type over
 * the MERGED text -- which is what they want to read -- and writes the merges
 * that are still standing back as tokens on save (see toTemplate). Storing
 * the merged text instead would bake this contact's name in at edit time:
 * right on the day it is typed, and a lie the day somebody corrects a
 * misspelled first name.
 *
 * EDITABILITY IS planEditLock(), NOT A SECOND RULE. A generated step has
 * already written a task somebody may be acting on; a skipped step recorded a
 * decision on a particular day. That is the same argument retime, skip and
 * remove make, so it is the same check -- and migration 081 enforces it in
 * the database besides, because this app is not that table's only client.
 */

import type { DataProvider } from "ra-core";

import { planEditLock } from "./planRules";
import { mergeValues, renderTemplate } from "./templates";
import type { ContentAsset } from "./ContentLibrary";
import type { EnrollmentStep } from "./types";

/** The four fields renderTemplate() understands, from a contact record. */
export type MergeableContact = {
  first_name?: string | null;
  last_name?: string | null;
  title?: string | null;
  company_name?: string | null;
};

/**
 * The merge values for one contact.
 *
 * `company` comes from `company_name`, which the contacts data provider
 * denormalizes onto the record -- the same map TouchButtons.tsx builds, kept
 * to the same four keys ContentLibrary advertises to authors.
 */
export function contactMergeValues(
  contact: MergeableContact | null | undefined,
): Record<string, unknown> {
  return {
    first_name: contact?.first_name,
    last_name: contact?.last_name,
    title: contact?.title,
    company: contact?.company_name,
  };
}

export type StepContent = {
  subject: string | null;
  body: string;
  /**
   * Where the words came from. `none` is a real state -- a bare step with no
   * asset and no override is legal, and saying "no content" is better than
   * rendering an empty box that looks like a loading failure.
   */
  source: "override" | "asset" | "none";
};

/**
 * What this step will actually say, and where that came from.
 *
 * THE SUBJECT AND BODY FALL BACK INDEPENDENTLY. Somebody who rewrote only the
 * subject must keep the asset's body, and a rule that treated "has an
 * override" as one flag would blank the half they did not touch.
 */
export function effectiveContent(
  step: Pick<EnrollmentStep, "subject" | "body">,
  asset: Pick<ContentAsset, "subject" | "body"> | null,
): StepContent {
  const body = step.body ?? asset?.body ?? "";
  const subject = step.subject ?? asset?.subject ?? null;
  const overridden = step.body != null || step.subject != null;

  if (overridden) return { subject, body, source: "override" };
  if (asset) return { subject, body, source: "asset" };
  return { subject: null, body: "", source: "none" };
}

/** Whether this step's content may still be changed, and why not if not. */
export function contentEditVerdict(step: Pick<EnrollmentStep, "status">) {
  return planEditLock(step);
}

/** The merged text an editor should open with, plus the merges to track. */
export function editorSeed(
  content: StepContent,
  values: Record<string, unknown>,
) {
  return {
    subject: renderTemplate(content.subject ?? "", values),
    body: renderTemplate(content.body, values),
    subjectMerges: mergeValues(content.subject ?? "", values),
    bodyMerges: mergeValues(content.body, values),
  };
}

/**
 * Write the override for ONE step.
 *
 * NOT THROUGH `content_assets`. That is the whole point of 081 and it is
 * worth restating at the call site: editing the library row would change the
 * words for every contact on every sequence that references it, including the
 * ones already sent.
 *
 * AN EMPTY STRING IS NULL. "I cleared this box" and "I want this step to say
 * nothing" are the same gesture, and the useful reading is the first --
 * clearing an override should fall back to the template rather than silently
 * committing the step to blank content.
 */
export async function saveStepContent(
  dataProvider: DataProvider,
  step: Pick<EnrollmentStep, "id" | "status">,
  next: { subject: string | null; body: string | null },
): Promise<void> {
  const lock = contentEditVerdict(step);
  if (lock.locked)
    throw new Error(
      lock.scope === "all"
        ? lock.reason
        : lock.refusals.map((r) => r.reason).join(" "),
    );

  const blankToNull = (v: string | null) => {
    const trimmed = (v ?? "").trim();
    return trimmed === "" ? null : v;
  };

  await dataProvider.update("enrollment_steps", {
    id: step.id,
    // `edited` is deliberately absent: migration 081's trigger stamps it, so
    // no client can write content and forget it. See propagation.ts:130.
    data: {
      subject: blankToNull(next.subject),
      body: blankToNull(next.body),
    },
    previousData: step,
  });
}
